// The folder store on the API: custom folders are shared by the whole team, kept in the database, and
// fetched per project in one request (folders with their item references). The interface is the one
// `createLocalFolderStore` implements (folders.ts); this file only knows the endpoint shapes.
//
// Reads are synchronous and come from a cache; writes go to the server first and update the cache with
// what the server answered, so the studio never shows a folder the server refused. While a project is
// open the cache is refreshed quietly (every 20 s while the tab is visible, and when it becomes visible),
// so other people's folders and drops show up without a reload.
import { type Api, ApiError } from "./api.ts";
import { rememberExtern } from "./data/imports-api.ts";
import {
  cleanName,
  descendantsOf,
  type Folder,
  type FolderEntry,
  FolderError,
  type FolderItemKind,
  type FolderItemRef,
  type FolderLoad,
  type FolderStore,
  restorable,
  sameRef,
} from "./folders.ts";

interface ApiItem {
  id: string;
  folder_id: string;
  item_type: string;
  item_id: string;
  position: number;
  created_at: string;
  /** an imported asset version: what came with it (Higgsfield import, F9b); absent on older servers */
  extern?: unknown;
}

interface ApiFolder {
  id: string;
  project_id: string;
  parent_id: string | null;
  name: string;
  position: number;
  created_at: string;
  archived_at: string | null;
  items?: ApiItem[];
}

/** The studio calls an asset version an "asset"; the API says what it is. */
const KIND_TO_API: Record<FolderItemKind, string> = {
  shot: "shot",
  canvas: "canvas",
  element: "element",
  asset: "asset_version",
  scene: "scene",
  episode: "episode",
};
const KIND_FROM_API = new Map<string, FolderItemKind>(
  Object.entries(KIND_TO_API).map(([ui, api]) => [api, ui as FolderItemKind]),
);

export function fromApi(f: ApiFolder): Folder {
  const items: FolderEntry[] = [];
  for (const i of [...(f.items ?? [])].sort((a, b) => a.position - b.position)) {
    const kind = KIND_FROM_API.get(i.item_type);
    // Takes and other kinds the studio has no card for are left out of the view, not lost on the server.
    if (kind) items.push({ kind, id: i.item_id, addedAt: i.created_at });
    if (kind === "asset" && i.extern) rememberExtern(i.item_id, i.extern);
  }
  return {
    id: f.id,
    projectId: f.project_id,
    parentId: f.parent_id,
    name: f.name,
    position: f.position,
    createdAt: f.created_at,
    archivedAt: f.archived_at,
    items,
  };
}

/** What went wrong, in the studio's words. */
export function folderErrorFrom(e: unknown): Error {
  if (e instanceof FolderError) return e;
  if (e instanceof ApiError) {
    if (e.status === 404) return new FolderError("not_found", "That folder no longer exists");
    if (e.status === 403)
      return new FolderError("forbidden", "You don’t have permission to change folders in this production");
    if (/cycle|inside itself|descendant/i.test(`${e.code} ${e.message}`))
      return new FolderError("cycle", "A folder cannot go inside itself");
    if (e.status === 400 || e.status === 422)
      return new FolderError("invalid_name", e.message || "That name can’t be used");
    return new FolderError("failed", e.message);
  }
  return e instanceof Error ? e : new Error(String(e));
}

export interface ApiStoreOptions {
  /** quiet refresh period while a project is open; 0 turns it off (tests) */
  refreshMs?: number;
}

const sig = (list: readonly Folder[]) => JSON.stringify(list);

export function createApiFolderStore(api: Api, ws: string, opts: ApiStoreOptions = {}): FolderStore {
  const refreshMs = opts.refreshMs ?? 20_000;
  const base = `/workspaces/${ws}/folders`;
  const byId = new Map<string, Folder>();
  /** project id → ids of its live folders, in server order */
  const order = new Map<string, string[]>();
  const states = new Map<string, FolderLoad>();
  const inflight = new Map<string, Promise<void>>();
  const snaps = new Map<string, Folder[]>();
  const asnaps = new Map<string, Folder[]>();
  /** project id → all its archived folders (a restore brings back the ones archived together) */
  const inArchive = new Map<string, Folder[]>();
  const watched = new Set<string>();
  const subs = new Set<() => void>();
  let writes = 0;
  let revision = 0;
  let reorderQueue = Promise.resolve();
  let timer: ReturnType<typeof setInterval> | null = null;
  const READY: FolderLoad = { status: "ready" };
  const IDLE: FolderLoad = { status: "idle" };
  const LOADING: FolderLoad = { status: "loading" };

  const emit = () => {
    snaps.clear();
    asnaps.clear();
    for (const fn of subs) fn();
  };
  const setState = (projectId: string, s: FolderLoad) => {
    states.set(projectId, s);
    emit();
  };

  const put = (f: Folder) => {
    byId.set(f.id, f);
    const ids = order.get(f.projectId) ?? [];
    if (!ids.includes(f.id)) order.set(f.projectId, [...ids, f.id]);
  };
  const drop = (id: string) => {
    const f = byId.get(id);
    byId.delete(id);
    if (f)
      order.set(
        f.projectId,
        (order.get(f.projectId) ?? []).filter((x) => x !== id),
      );
  };
  const need = (id: string): Folder => {
    const f = byId.get(id);
    if (!f) throw new FolderError("not_found", "That folder no longer exists");
    return f;
  };
  const live = (projectId: string): Folder[] =>
    (order.get(projectId) ?? []).map((id) => byId.get(id)).filter((f): f is Folder => !!f && !f.archivedAt);

  async function fetchProject(projectId: string, mode: "visible" | "quiet" | "force"): Promise<void> {
    const startedAt = revision;
    const writesAtStart = writes;
    const prev = states.get(projectId);
    if (mode === "visible") setState(projectId, LOADING);
    try {
      const res = await api.request<{ items: ApiFolder[] }>("GET", base, {
        query: { projectId, withItems: "true", includeArchived: "true" },
      });
      // A write finished (or started) meanwhile: its answer is newer than this list, so ask again later.
      if (startedAt !== revision || (mode !== "force" && (writesAtStart > 0 || writes > 0))) {
        if (mode === "force") return fetchProject(projectId, mode);
        if (mode === "visible") setState(projectId, prev ?? IDLE);
        return;
      }
      const all = res.items.map(fromApi);
      const next = all.filter((f) => !f.archivedAt);
      const nextGone = all.filter((f) => f.archivedAt);
      const changed =
        sig(next) !== sig(live(projectId)) || sig(nextGone) !== sig(inArchive.get(projectId) ?? []);
      inArchive.set(projectId, nextGone);
      for (const id of order.get(projectId) ?? []) byId.delete(id);
      order.set(projectId, []);
      for (const f of next) put(f);
      if (changed || states.get(projectId)?.status !== "ready") setState(projectId, READY);
    } catch (e) {
      // Restore must report a failed reconciliation instead of treating a missing cache entry as deleted.
      if (mode === "force") throw e;
      // A quiet refresh that fails keeps what we have; only a visible load reports the error.
      if (mode !== "visible") return;
      setState(projectId, {
        status: "error",
        error: e instanceof Error ? e.message : String(e),
      });
      if (prev?.status === "ready") states.set(projectId, prev);
    }
  }

  const tick = () => {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
    for (const p of watched) if (!inflight.has(p)) void fetchProject(p, "quiet");
  };
  const onVisible = () => {
    if (document.visibilityState === "visible") tick();
  };
  const start = () => {
    if (timer || refreshMs <= 0 || typeof document === "undefined") return;
    timer = setInterval(tick, refreshMs);
    document.addEventListener("visibilitychange", onVisible);
  };
  const stop = () => {
    if (!timer) return;
    clearInterval(timer);
    timer = null;
    watched.clear();
    document.removeEventListener("visibilitychange", onVisible);
  };

  /** Run a write: count it so a quiet refresh does not overwrite its result, map errors to the studio's. */
  async function write<T>(fn: () => Promise<T>): Promise<T> {
    writes++;
    revision++;
    try {
      return await fn();
    } catch (e) {
      throw folderErrorFrom(e);
    } finally {
      writes--;
      revision++;
    }
  }

  return {
    list(projectId) {
      let l = snaps.get(projectId);
      if (!l) {
        l = live(projectId);
        snaps.set(projectId, l);
      }
      return l;
    },
    archived(projectId) {
      let l = asnaps.get(projectId);
      if (!l) {
        l = restorable(inArchive.get(projectId) ?? []);
        asnaps.set(projectId, l);
      }
      return l;
    },
    get: (id) => {
      const f = byId.get(id);
      return f && !f.archivedAt ? f : undefined;
    },
    state: (projectId) => states.get(projectId) ?? IDLE,
    load(projectId) {
      watched.add(projectId);
      start();
      let p = inflight.get(projectId);
      if (!p) {
        p = fetchProject(projectId, "visible").finally(() => inflight.delete(projectId));
        inflight.set(projectId, p);
      }
      return p;
    },
    create: (projectId, name, parentId = null) =>
      write(async () => {
        const clean = cleanName(name);
        const f = fromApi(
          await api.request<ApiFolder>("POST", base, { body: { projectId, parentId, name: clean } }),
        );
        put(f);
        emit();
        return f;
      }),
    rename: (id, name) =>
      write(async () => {
        const clean = cleanName(name);
        need(id);
        const updated = fromApi(
          await api.request<ApiFolder>("PATCH", `${base}/${id}`, { body: { name: clean } }),
        );
        put({ ...need(id), name: updated.name });
        emit();
        return need(id);
      }),
    move: (id, parentId) =>
      write(async () => {
        const f = need(id);
        if (parentId) {
          const p = need(parentId);
          if (p.projectId !== f.projectId)
            throw new FolderError("not_found", "Parent is in another production");
          if (parentId === id || descendantsOf(live(f.projectId), id).has(parentId))
            throw new FolderError("cycle", "A folder cannot go inside itself");
        }
        const res = await api.request<ApiFolder>("PATCH", `${base}/${id}`, { body: { parentId } });
        // The server puts a moved folder last in its new parent: take that position.
        put({ ...need(id), parentId, position: fromApi(res).position });
        emit();
        return need(id);
      }),
    reorder: (projectId, parentId, ids) => {
      const result = reorderQueue.then(() =>
        write(async () => {
          const siblings = ids.map((id) => {
            const f = need(id);
            if (f.projectId !== projectId || f.parentId !== parentId)
              throw new FolderError("not_found", "Those folders are not in the same place");
            return f;
          });
          try {
            for (const [index, f] of siblings.entries()) {
              if (need(f.id).position === index) continue;
              const res = await api.request<ApiFolder>("PATCH", `${base}/${f.id}`, {
                body: { position: index },
              });
              put({ ...need(f.id), position: fromApi(res).position });
              emit();
            }
          } catch (e) {
            // Some positions may have been written: show what the server has, then report the failure.
            await fetchProject(projectId, "force").catch(() => undefined);
            throw e;
          }
        }),
      );
      reorderQueue = result.catch(() => undefined);
      return result;
    },
    archive: (id) =>
      write(async () => {
        const f = need(id);
        const res = await api.request<ApiFolder>("POST", `${base}/${id}/archive`);
        // The server archived the subtree together, at one moment; mirror that locally.
        const at = fromApi(res).archivedAt ?? new Date().toISOString();
        const subtree = descendantsOf(live(f.projectId), id).add(id);
        const moved = [...subtree].map((g) => ({ ...need(g), archivedAt: at }));
        for (const g of subtree) drop(g);
        inArchive.set(f.projectId, [...(inArchive.get(f.projectId) ?? []), ...moved]);
        emit();
      }),
    restore: (id) =>
      write(async () => {
        const res = await api.request<ApiFolder>("POST", `${base}/${id}/restore`);
        // The restored subtree comes back with its items: take the project list again.
        await fetchProject(res.project_id, "force");
        const f = byId.get(id);
        if (!f) throw new FolderError("not_found", "That folder no longer exists");
        return f;
      }),
    addItem: (id, ref) =>
      write(async () => {
        const f = need(id);
        if (f.items.some((i) => sameRef(i, ref))) return f;
        await api.request("POST", `${base}/${id}/items`, {
          body: { items: [{ type: KIND_TO_API[ref.kind], id: ref.id }] },
        });
        const current = need(id);
        if (!current.items.some((i) => sameRef(i, ref)))
          put({
            ...current,
            items: [...current.items, { kind: ref.kind, id: ref.id, addedAt: new Date().toISOString() }],
          });
        emit();
        return need(id);
      }),
    removeItem: (id, ref: FolderItemRef) =>
      write(async () => {
        need(id);
        await api.request("POST", `${base}/${id}/items/remove`, {
          body: { items: [{ type: KIND_TO_API[ref.kind], id: ref.id }] },
        });
        const current = need(id);
        put({ ...current, items: current.items.filter((i) => !sameRef(i, ref)) });
        emit();
        return need(id);
      }),
    subscribe(fn) {
      subs.add(fn);
      return () => {
        subs.delete(fn);
        if (subs.size === 0) stop();
      };
    },
  };
}
